import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { formatDate } from '../../utils';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import inquiryService from '../../services/inquiryService';
import vendorDataService from '../../services/vendorDataService';

const MessageDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const messageId = location.state?.messageId;
  const vendorId = location.state?.vendorId;
  const vendorName = location.state?.vendorName;
  const category = location.state?.category;

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');

  // Load inquiry and vendor details from Supabase
  useEffect(() => {
    const loadMessageDetails = async () => {
      if (!messageId || !user?.id) {
        navigate('/user-dashboard');
        return;
      }

      setIsLoading(true);
      try {
        // Fetch inquiry details
        const inquiryResult = await inquiryService.getInquiryById(messageId);

        if (inquiryResult.success && inquiryResult.data) {
          const inquiry = inquiryResult.data;

          // Fetch vendor details if not provided
          let vendor = null;
          if (vendorId || inquiry.vendor_id) {
            const vendorResult = await vendorDataService.getVendorById(vendorId || inquiry.vendor_id);
            if (vendorResult.success) {
              vendor = vendorResult.data;
            }
          }

          // Fetch all conversation messages
          const messagesResult = await inquiryService.getConversationMessages(messageId);

          let conversation = [];

          // Add the initial inquiry message
          conversation.push({
            id: 'initial',
            sender: 'user',
            text: inquiry.message || inquiry.subject || 'Inquiry',
            timestamp: inquiry.created_at,
            read: true
          });

          // Add all conversation messages
          if (messagesResult.success && messagesResult.data) {
            messagesResult.data.forEach((msg, index) => {
              conversation.push({
                id: msg.id,
                sender: msg.sender_type,
                text: msg.message_text,
                timestamp: msg.created_at,
                read: msg.is_read
              });
            });
          }

          // Format message data
          setMessage({
            id: inquiry.id,
            vendorId: inquiry.vendor_id,
            vendorName: vendor?.business_name || vendorName || 'Unknown Vendor',
            category: vendor?.category || category || 'General',
            avatar: vendor?.profile_image || `https://ui-avatars.com/api/?name=${vendor?.business_name || 'V'}&background=8B1538&color=fff`,
            phone: vendor?.phone || '',
            conversation
          });

          // Mark messages as read
          await inquiryService.markMessagesAsRead(messageId);
        }
      } catch (error) {
        console.error('Error loading message details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMessageDetails();
  }, [messageId, vendorId, vendorName, category, user, navigate]); const handleSendMessage = async () => {
    if (!newMessage.trim() || !message) return;

    try {
      // Send message to Supabase
      const result = await inquiryService.sendMessage(messageId, newMessage, 'user');

      if (result.success) {
        // Add the new message to the conversation
        const newMsg = {
          id: result.data.id,
          sender: 'user',
          text: newMessage,
          timestamp: new Date().toISOString(),
          read: false
        };

        setMessage(prev => ({
          ...prev,
          conversation: [...prev.conversation, newMsg]
        }));

        setNewMessage('');
        showToast('Message sent successfully', 'success');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showToast('Failed to send message. Please try again.', 'error');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 pt-20 pb-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading conversation...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!message) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 pt-20 pb-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center py-12">
              <Icon name="MessageSquare" size={48} className="text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary mb-4">Message not found</p>
              <Button
                variant="primary"
                onClick={() => navigate('/user-dashboard')}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 pt-20 pb-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-soft p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/user-dashboard')}
                  className="p-2 hover:bg-surface-100 rounded-lg transition-all"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <img
                  src={message.avatar}
                  alt={message.vendorName}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h2 className="font-semibold text-text-primary">{message.vendorName}</h2>
                  <p className="text-sm text-text-secondary">{message.category}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Phone"
                  onClick={() => {
                    const phoneNumber = message.phone || '';
                    if (phoneNumber) {
                      // Open phone dialer
                      window.location.href = `tel:${phoneNumber}`;
                    } else {
                      showToast('Phone number not available for this vendor', 'warning');
                    }
                  }}
                >
                  Call
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="User"
                  onClick={() => {
                    // Navigate to vendor profile with vendor ID, not message ID
                    const vendorId = message.vendorId || message.id;
                    navigate(`/vendor-profile/${vendorId}`);
                  }}
                >
                  View Profile
                </Button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white rounded-xl shadow-soft p-6 mb-4" style={{ minHeight: '500px', maxHeight: '600px', overflowY: 'auto' }}>
            <div className="space-y-4">
              {message.conversation.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${msg.sender === 'user' ? 'order-2' : 'order-1'}`}>
                    <div className={`rounded-lg p-4 ${msg.sender === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-surface-100 text-text-primary'
                      }`}>
                      <p className="whitespace-pre-wrap">{msg.text}</p>
                    </div>
                    <div className={`flex items-center space-x-2 mt-1 text-xs text-text-muted ${msg.sender === 'user' ? 'justify-end' : 'justify-start'
                      }`}>
                      <span>{formatDate(msg.timestamp)}</span>
                      {msg.sender === 'user' && (
                        <Icon name={msg.read ? "CheckCheck" : "Check"} size={14} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div className="bg-white rounded-xl shadow-soft p-4">
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="w-full p-3 border border-border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows="3"
                />
              </div>
              <Button
                variant="primary"
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                iconName="Send"
              >
                Send
              </Button>
            </div>
            <div className="flex items-center space-x-4 mt-3">
              <button className="text-text-muted hover:text-primary transition-all">
                <Icon name="Paperclip" size={20} />
              </button>
              <button className="text-text-muted hover:text-primary transition-all">
                <Icon name="Image" size={20} />
              </button>
              <button className="text-text-muted hover:text-primary transition-all">
                <Icon name="Smile" size={20} />
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MessageDetail;
